import { Editor, Element as SlateElement, Transforms, Range } from 'slate';
import { v4 as uuidv4 } from 'uuid';
import { CustomEditor } from 'slate/types/slate';
import { LIST_TYPES } from './constants';

export const isListActive = (editor: CustomEditor, format: string) => {
  const [match] = Editor.nodes(editor, {
    match: (n) => SlateElement.isElement(n) && n.type === format,
  });
  return !!match;
};

export const toggleList = (editor: CustomEditor, format: string) => {
  if (!editor.selection) return;

  const isListFormat = LIST_TYPES.includes(format);

  // Find list-item and parent list around the current selection (if any)
  const [listItemEntry] = Editor.nodes(editor, {
    match: (n) => SlateElement.isElement(n) && n.type === 'list-item',
  });

  const [parentListEntry] = Editor.nodes(editor, {
    match: (n) => SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
  });

  const inListItem = !!listItemEntry;
  const parentListNode = parentListEntry?.[0];
  const currentListType = parentListNode && SlateElement.isElement(parentListNode) ? parentListNode.type : null;

  Editor.withoutNormalizing(editor, () => {
    if (inListItem) {
      if (isListFormat) {
        if (currentListType === format) {
          const [, liPath] = listItemEntry;
          Transforms.setNodes(editor, { type: 'paragraph' }, { at: liPath });
          Transforms.unwrapNodes(editor, {
            match: (n) => SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
            split: true,
          });
        } else {
          const [, listPath] = parentListEntry;
          Transforms.setNodes(editor, { type: format as 'bulleted-list' | 'numbered-list' }, { at: listPath });
        }
      } else {
        const [, liPath] = listItemEntry;
        Transforms.setNodes(editor, { type: 'paragraph' }, { at: liPath });
        Transforms.unwrapNodes(editor, {
          match: (n) => SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
          split: true,
        });
      }
    } else {
      if (isListFormat) {
        wrapParagraphsIntoList(editor, format);
      } else {
        Transforms.setNodes(
          editor,
          { type: 'paragraph' },
          {
            match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
            split: true,
          }
        );
      }
    }
  });
};

const wrapParagraphsIntoList = (editor: CustomEditor, format: string) => {
  if (!editor.selection) return;

  if (!LIST_TYPES.includes(format)) return;

  // Get all selected blocks
  const blocks = Array.from(
    Editor.nodes(editor, {
      at: editor.selection,
      match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n) && n.type === 'paragraph',
      mode: 'lowest',
    })
  );
  if (!blocks.length) return;

  Editor.withoutNormalizing(editor, () => {
    const { selection } = editor;
    if (!selection) return;
    const [start, end] = Range.edges(selection);

    // Find the first and last paragraph fully or partially in selection
    const startBlock = Editor.above(editor, {
      at: start,
      match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
    });
    const endBlock = Editor.above(editor, {
      at: end,
      match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
    });

    if (!startBlock || !endBlock) return;

    const [, startPath] = startBlock;
    const [, endPath] = endBlock;

    // Collect all block nodes between the start and end paragraphs (inclusive)
    const rangeBlocks = Array.from(
      Editor.nodes(editor, {
        at: Editor.range(editor, startPath, endPath),
        match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
        mode: 'lowest',
      })
    );

    if (rangeBlocks.length === 0) return;

    // Unwrap existing lists within the range
    Transforms.unwrapNodes(editor, {
      match: (n) => SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
      at: Editor.range(editor, startPath, endPath),
      split: true,
    });

    // Convert paragraphs to list-items
    for (const [, path] of rangeBlocks) {
      Transforms.setNodes(editor, { type: 'list-item' }, { at: path });
    }

    // Wrap all list-items in a list container
    const list = { type: format as 'bulleted-list' | 'numbered-list', uuid: uuidv4(), children: [] };
    Transforms.wrapNodes(editor, list, {
      at: Editor.range(editor, startPath, endPath),
      match: (n) => SlateElement.isElement(n) && n.type === 'list-item',
      split: true,
    });
  });
};
